import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  RiLockPasswordLine,
  RiEyeLine,
  RiEyeOffLine,
  RiArrowLeftLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiTimeLine,
} from 'react-icons/ri';
import authService from '../../services/authService';
// KD3 S2376: timeout/offline/5xx ไม่มี envelope เดิมโชว์ข้อความ axios ภาษาอังกฤษ ("timeout of 30000ms exceeded") → helper กลางแปลเป็นไทย (P-S043-1)
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasDigit: boolean;
}

const validatePassword = (password: string): PasswordValidation => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasDigit: /[0-9]/.test(password),
});

const isPasswordValid = (v: PasswordValidation): boolean =>
  v.minLength && v.hasUppercase && v.hasLowercase && v.hasDigit;

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  // เอ๊ะ #5 (PO-approved 2026-08-14): ถ้า token ใช้ไปแล้ว/หมดอายุ ฟอร์มนี้ช่วยอะไรไม่ได้อีก
  // — กรอกใหม่กี่รอบก็ 400 เหมือนเดิม. แยก state นี้ไว้เพื่อซ่อนฟอร์มแล้วเหลือแต่ทางออกจริง
  const [tokenDead, setTokenDead] = useState(false);
  const [countdown, setCountdown] = useState(3);

  // BUG-AUTH-001-DBLLOGIN FIX (sibling): double-click ก่อน re-render → resetPassword ยิง 2 ครั้ง
  // ครั้งแรกสำเร็จ (token ถูกเผา) ครั้งที่สองเด้ง 'ลิงก์ถูกใช้งานแล้ว' ทับหน้าสำเร็จ = ผู้ใช้งง
  const inFlightRef = useRef(false);

  const validation = validatePassword(newPassword);

  // นับถอยหลัง redirect หลัง success
  useEffect(() => {
    if (!isSuccess) return;
    if (countdown <= 0) {
      navigate('/login');
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [isSuccess, countdown, navigate]);

  // ถ้าไม่มี token ใน URL แสดง error ทันที
  if (!token) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4" aria-label="หน้ารีเซ็ตรหัสผ่าน">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4 shadow-lg">
              <span className="text-white font-bold text-2xl">K</span>
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">KRP System</h1>
          </div>
          <div className="bg-white rounded-2xl shadow-xl border border-secondary-100 p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-danger-100 rounded-full mb-4">
              <RiErrorWarningLine className="w-9 h-9 text-danger-600" />
            </div>
            <h2 className="text-xl font-bold text-secondary-900 mb-2">ลิงก์ไม่ถูกต้อง</h2>
            <p className="text-secondary-600 text-sm mb-6">
              ลิงก์รีเซ็ตรหัสผ่านไม่ถูกต้องหรือหมดอายุแล้ว
              กรุณาขอลิงก์ใหม่อีกครั้ง
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 min-h-[44px] sm:min-h-[38px] py-2 rounded-md font-medium text-sm transition-colors"
            >
              ขอลิงก์ใหม่
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlightRef.current) return; // race-loser drop
    setError('');

    if (!isPasswordValid(validation)) {
      setError('รหัสผ่านไม่ตรงตามเงื่อนไขที่กำหนด');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('รหัสผ่านทั้งสองช่องไม่ตรงกัน');
      return;
    }

    inFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await authService.resetPassword(token, newPassword);
      setIsSuccess(true);
    } catch (err: any) {
      // BUG-S661-001 FIX: read structured error wrapper (error.user_message) for Thai user-friendly text
      const message =
        err?.response?.data?.error?.user_message ||
        err?.response?.data?.detail ||
        getApiErrorMessage(err, 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      setError(message);
      // เอ๊ะ #5: backend บอกเองว่า "กรุณาขอลิงก์ใหม่" เมื่อลิงก์ถูกใช้แล้วหรือหมดอายุ
      // (auth.py reset-password: 'ลิงก์รีเซ็ตรหัสผ่านนี้ถูกใช้งานแล้ว…' / 'ไม่ถูกต้องหรือหมดอายุ')
      // จับจากข้อความเพราะ error code เป็น BUSINESS_RULE_VIOLATION ร่วมกับเคสอื่น
      if (typeof message === 'string' && /ถูกใช้งานแล้ว|หมดอายุ|ไม่ถูกต้อง/.test(message)) {
        setTokenDead(true);
      }
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const ValidationRow: React.FC<{ passed: boolean; text: string }> = ({ passed, text }) => (
    <div className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-secondary-400'}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${passed ? 'bg-green-500' : 'bg-secondary-300'}`} />
      {text}
    </div>
  );

  return (
    <main className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4" aria-label="หน้ารีเซ็ตรหัสผ่าน">
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">K</span>
          </div>
          <h1 className="text-2xl font-bold text-secondary-900">KRP System</h1>
          <p className="text-secondary-500 text-sm mt-1">ระบบจัดการการเช่าอุปกรณ์</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-secondary-100 p-8">
          {isSuccess ? (
            /* Success State */
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <RiCheckboxCircleLine className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-secondary-900 mb-2">รีเซ็ตรหัสผ่านสำเร็จ</h2>
              <p className="text-secondary-600 text-sm mb-4">
                รหัสผ่านของคุณได้รับการอัปเดตเรียบร้อยแล้ว
                กรุณาเข้าสู่ระบบด้วยรหัสผ่านใหม่
              </p>
              <div className="flex items-center justify-center gap-2 text-secondary-500 text-sm mb-6">
                <RiTimeLine className="w-4 h-4" />
                <span>กำลังนำไปหน้าเข้าสู่ระบบใน {countdown} วินาที...</span>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 min-h-[44px] sm:min-h-[38px] py-2 rounded-md font-medium text-sm transition-colors"
              >
                <RiArrowLeftLine className="w-4 h-4" />
                ไปหน้าเข้าสู่ระบบเลย
              </Link>
            </div>
          ) : (
            /* Form State */
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-secondary-900">ตั้งรหัสผ่านใหม่</h2>
                <p className="text-secondary-500 text-sm mt-1">
                  กรอกรหัสผ่านใหม่ของคุณด้านล่าง
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {error && (
                  <div className="flex items-start gap-3 bg-danger-50 border border-danger-300 text-danger-700 px-4 py-3 rounded-lg" role="alert">
                    <RiErrorWarningLine className="w-5 h-5 mt-0.5 flex-shrink-0 text-danger-500" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">{error}</p>
                      {/* เอ๊ะ #5: ข้อความบอกให้ "ขอลิงก์ใหม่" — ให้ทางไปด้วย ไม่ใช่ปล่อยให้ไปหาเอง */}
                      {tokenDead && (
                        <Link
                          to="/forgot-password"
                          className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                        >
                          ขอลิงก์ใหม่
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {/* เอ๊ะ #5: ลิงก์ตายแล้ว → ซ่อนฟอร์มทิ้ง กันผู้ใช้กรอกซ้ำแล้วโดน 400 อีกรอบ */}
                {!tokenDead && (
                <>
                {/* New Password */}
                <div className="space-y-2">
                  <label htmlFor="newPassword" className="block text-sm font-medium text-secondary-700">
                    รหัสผ่านใหม่
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiLockPasswordLine className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      maxLength={128}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (error) setError('');
                      }}
                      disabled={isSubmitting}
                      className="input w-full h-11 pl-10 pr-11 text-secondary-900"
                      placeholder="กรอกรหัสผ่านใหม่"
                      autoComplete="new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label={showNewPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                      className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-secondary-500 hover:text-secondary-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                    >
                      {showNewPassword ? (
                        <RiEyeOffLine className="w-5 h-5" aria-hidden="true" />
                      ) : (
                        <RiEyeLine className="w-5 h-5" aria-hidden="true" />
                      )}
                    </button>
                  </div>

                  {/* Password validation hints */}
                  {newPassword.length > 0 && (
                    <div className="mt-2 space-y-1 p-3 bg-secondary-50 rounded-lg">
                      <ValidationRow passed={validation.minLength} text="อย่างน้อย 8 ตัวอักษร" />
                      <ValidationRow passed={validation.hasUppercase} text="ตัวพิมพ์ใหญ่อย่างน้อย 1 ตัว (A-Z)" />
                      <ValidationRow passed={validation.hasLowercase} text="ตัวพิมพ์เล็กอย่างน้อย 1 ตัว (a-z)" />
                      <ValidationRow passed={validation.hasDigit} text="ตัวเลขอย่างน้อย 1 ตัว (0-9)" />
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-secondary-700">
                    ยืนยันรหัสผ่านใหม่
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiLockPasswordLine className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      maxLength={128}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (error) setError('');
                      }}
                      disabled={isSubmitting}
                      className={`input w-full h-11 pl-10 pr-11 text-secondary-900 ${
                        confirmPassword.length > 0 && confirmPassword !== newPassword
                          ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500'
                          : ''
                      }`}
                      placeholder="กรอกรหัสผ่านอีกครั้ง"
                      autoComplete="new-password"
                      // BUG-A11Y-FORMFIELD-ARIA (incremental · CRIT auth form): bind the
                      // mismatch error to the field so screen-readers announce it (WCAG 3.3.1)
                      aria-invalid={confirmPassword.length > 0 && confirmPassword !== newPassword}
                      aria-describedby={confirmPassword.length > 0 && confirmPassword !== newPassword ? 'confirmPassword-error' : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                      className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-secondary-500 hover:text-secondary-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                    >
                      {showConfirmPassword ? (
                        <RiEyeOffLine className="w-5 h-5" aria-hidden="true" />
                      ) : (
                        <RiEyeLine className="w-5 h-5" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                    <p id="confirmPassword-error" className="mt-1 text-sm text-danger-600">รหัสผ่านไม่ตรงกัน</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !isPasswordValid(validation) || newPassword !== confirmPassword}
                  className="w-full h-11 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                      กำลังบันทึก...
                    </>
                  ) : (
                    <>
                      <RiLockPasswordLine className="w-5 h-5 mr-2" />
                      ตั้งรหัสผ่านใหม่
                    </>
                  )}
                </button>
                </>
                )}
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                >
                  <RiArrowLeftLine className="w-4 h-4" />
                  กลับหน้าเข้าสู่ระบบ
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
